import EXTENT from '../../../src/style-spec/data/extent';
import {register} from '../../../src/util/web_worker_transfer';
import loadGeometry from '../../../src/data/load_geometry';
import toEvaluationFeature from '../../../src/data/evaluation_feature';
import EvaluationParameters from '../../../src/style/evaluation_parameters';
import {vec3} from 'gl-matrix';
import {InstanceVertexArray} from '../../../src/data/array_types';
import assert from 'assert';
import {warnOnce} from '../../../src/util/util';
import {rotationScaleYZFlipMatrix} from '../../util/model_util';
import {tileToMeter} from '../../../src/geo/mercator_coordinate';
import {instanceAttributes} from '../model_attributes';
import {regionsEquals, transformPointToTile, pointInFootprint, skipClipping} from '../../../3d-style/source/replacement_source';
import {LayerTypeMask} from '../../../3d-style/util/conflation';
import {isValidUrl} from '../../../src/style-spec/validate/validate_model';
import {type FeatureState, type GlobalProperties} from '../../../src/style-spec/expression/index';
import Point from '@mapbox/point-geometry';

import type ModelStyleLayer from '../../style/style_layer/model_style_layer';
import type {ReplacementSource, Region} from '../../../3d-style/source/replacement_source';
import type {EvaluationFeature} from '../../../src/data/evaluation_feature';
import type {mat3, mat4} from 'gl-matrix';
import type {CanonicalTileID, OverscaledTileID, UnwrappedTileID} from '../../../src/source/tile_id';
import type {
    Bucket,
    BucketParameters,
    BucketFeature,
    IndexedFeature,
    PopulateParameters
} from '../../../src/data/bucket';
import type Context from '../../../src/gl/context';
import type VertexBuffer from '../../../src/gl/vertex_buffer';
import type {FeatureStates} from '../../../src/source/source_state';
import type {SpritePositions} from '../../../src/util/image';
import type {ProjectionSpecification} from '../../../src/style-spec/types';
import type {TileTransform} from '../../../src/geo/projection/tile_transform';
import type {VectorTileLayer} from '@mapbox/vector-tile';
import type {TileFootprint} from '../../../3d-style/util/conflation';
import type {ImageId} from '../../../src/style-spec/expression/types/image_id';
import type {StyleModelMap} from '../../../src/style/style_mode';

class ModelFeature {
    feature: EvaluationFeature;
    featureStates: FeatureState;
    instancedDataOffset: number;
    instancedDataCount: number;

    rotation: vec3;
    scale: vec3;
    translation: vec3;

    constructor(feature: EvaluationFeature, offset: number) {
        this.feature = feature;
        this.instancedDataOffset = offset;
        this.instancedDataCount = 0;
        this.rotation = [0, 0, 0];
        this.scale = [1, 1, 1];
        this.translation = [0, 0, 0];
    }
}

class PerModelAttributes {
    // If node has meshes, instancedDataArray gets an entry for each feature instance (used for all meshes or the node).
    instancedDataArray: InstanceVertexArray;
    instancedDataBuffer: VertexBuffer;
    instancesEvaluatedElevation: Array<number>; // Gets added to DEM elevation of the instance to produce value in instancedDataArray.

    features: Array<ModelFeature>;
    idToFeaturesIndex: Partial<Record<string | number, number>>; // via this.features, enable lookup instancedDataArray based on feature ID.
    maxScale: number = 1;
    maxXYTranslationDistance: number = 0;

    constructor() {
        this.instancedDataArray = new InstanceVertexArray();
        this.instancesEvaluatedElevation = [];
        this.features = [];
        this.idToFeaturesIndex = {};
    }

    colorForInstance(index: number): [number, number, number, number] {
        const offset = index * 16;
        const va = this.instancedDataArray.float32;

        // Decompose: Math.round(100.0 * color.a) + color.b / 1.05;
        let a = Math.floor(va[offset + 2]);
        const b = (va[offset + 2] - a) * 1.05;
        a /= 100.0;
        // 0 & 1: tile coordinates stored in integer part of float, R and G color components,
        // originally in range [0..1], scaled to range [0..0.952(arbitrary, just needs to be
        // under 1)].
        const r = (va[offset] % 1) * 1.05;
        const g = (va[offset + 1] % 1) * 1.05;
        return [r, g, b, a];
    }

    tileCoordinatesForInstance(index: number): Point {
        const offset = index * 16;
        const va = this.instancedDataArray.float32;
        let x_ = va[offset + 0];
        const wasHidden = x_ > EXTENT;
        x_ = wasHidden ? x_ - EXTENT : x_;
        // Position is stored in integer part of value, fractional part is used for color
        const x = Math.trunc(x_);
        const y = Math.trunc(va[offset + 1]);
        return new Point(x, y);
    }

    translationForInstance(index: number): vec3 {
        const offset = index * 16;
        const va = this.instancedDataArray.float32;
        return [va[offset + 4], va[offset + 5], va[offset + 6]];
    }

    rotationScaleForInstance(index: number): mat3 {
        const offset = index * 16;
        const va = this.instancedDataArray.float32;
        return [va[offset + 7],
            va[offset + 8],
            va[offset + 9],
            va[offset + 10],
            va[offset + 11],
            va[offset + 12],
            va[offset + 13],
            va[offset + 14],
            va[offset + 15]];
    }

    transformForInstance(index: number): mat4 {
        const offset = index * 16;
        const va = this.instancedDataArray.float32;
        return [
            va[offset + 7], va[offset + 8], va[offset + 9], va[offset + 4],
            va[offset + 10], va[offset + 11], va[offset + 12], va[offset + 5],
            va[offset + 13], va[offset + 14], va[offset + 15], va[offset + 6],
            0, 0, 0, 1.0];
    }
}

class ModelBucket implements Bucket {
    zoom: number;
    index: number;
    canonical: CanonicalTileID;
    overscaledZ: number;
    layers: Array<ModelStyleLayer>;
    layerIds: Array<string>;
    stateDependentLayers: Array<ModelStyleLayer>;
    stateDependentLayerIds: Array<string>;
    hasPattern: boolean;
    worldview: string | undefined;

    instancesPerModel: Record<string, PerModelAttributes>;

    uploaded: boolean;

    tileToMeter: number;
    projection: ProjectionSpecification;

    // elevation is baked into vertex buffer together with evaluated instance translation
    validForExaggeration: number;
    validForDEMTile: {
        id: OverscaledTileID | null | undefined;
        timestamp: number;
    };
    maxVerticalOffset: number; // for tile AABB calculation
    maxScale: number; // across all dimensions, for tile AABB calculation
    maxHeight: number; // calculated from previous two, during rendering, when models are available.
    isInsideFirstShadowMapFrustum: boolean; // evaluated during first shadows pass and cached here for the second shadow pass.
    lookup: Uint8Array | null | undefined;
    lookupDim: number;
    instanceCount: number;
    // Bucket min/max terrain elevation among instance positions taking exaggeration value into account
    terrainElevationMin: number;
    terrainElevationMax: number;

    hasZoomDependentProperties: boolean;
    modelUris: Array<string>;
    modelsRequested: boolean;

    activeReplacements: Array<Region>;
    replacementUpdateTime: number;
    styleDefinedModelURLs: StyleModelMap;
    hasAppearances: boolean | null;

    constructor(options: BucketParameters<ModelStyleLayer>) {
        this.zoom = options.zoom;
        this.canonical = options.canonical;
        this.overscaledZ = this.canonical.z + Math.log2(options.overscaling);
        this.layers = options.layers;
        this.layerIds = this.layers.map(layer => layer.fqid);
        this.projection = options.projection;
        this.index = options.index;
        this.worldview = options.worldview;

        this.hasZoomDependentProperties = this.layers[0].isZoomDependent();

        this.stateDependentLayerIds = this.layers.filter((l) => l.isStateDependent()).map((l) => l.id);
        this.hasPattern = false;
        this.instancesPerModel = {};
        this.validForExaggeration = 0;
        this.maxVerticalOffset = 0;
        this.maxScale = 0;
        this.maxHeight = 0;
        // reduce density, more on lower zooms and almost no reduction in overscale range.
        // Heuristics is related to trees performance. Disable density check after maxZoom + 2.
        // For vector tiles this means:
        // z15 and lower: lookup grid of size 75x75 per tile
        // z16: 100x100, z17: 256x256, z18: no density reduction.
        this.lookupDim = this.zoom > (this.canonical.z + 1) ? 0 : (this.zoom > this.canonical.z ? 256 : this.zoom > 15 ? 75 : 100);
        this.instanceCount = 0;

        this.terrainElevationMin = 0;
        this.terrainElevationMax = 0;
        this.validForDEMTile = {id: null, timestamp: 0};
        this.modelUris = [];
        this.modelsRequested = false;
        this.activeReplacements = [];
        this.replacementUpdateTime = 0;
        this.styleDefinedModelURLs = options.styleDefinedModelURLs;
        this.hasAppearances = null;
    }

    updateFootprints(_id: UnwrappedTileID, _footprints: Array<TileFootprint>) {
    }

    updateAppearances(_canonical?: CanonicalTileID, _featureState?: FeatureStates, _availableImages?: Array<ImageId>, _globalProperties?: GlobalProperties) {
    }

    populate(features: Array<IndexedFeature>, options: PopulateParameters, canonical: CanonicalTileID, tileTransform: TileTransform) {
        this.tileToMeter = tileToMeter(canonical);
        const needGeometry = this.layers[0]._featureFilter.needGeometry;
        this.lookup = new Uint8Array(this.lookupDim * this.lookupDim);

        for (const {feature, id, index, sourceLayerIndex} of features) {
            // use non numeric id, if in properties, too.
            const featureId = (id != null) ? id :
                (feature.properties && feature.properties.hasOwnProperty("id")) ? feature.properties["id"] : undefined;
            const evaluationFeature = toEvaluationFeature(feature, needGeometry);

            if (!this.layers[0]._featureFilter.filter(new EvaluationParameters(this.zoom, {worldview: this.worldview, activeFloors: options.activeFloors}), evaluationFeature, canonical))
                continue;

            const bucketFeature: BucketFeature = {
                id: featureId as number,
                sourceLayerIndex,
                index,
                geometry: needGeometry ? evaluationFeature.geometry : loadGeometry(feature, canonical, tileTransform),
                properties: feature.properties,
                type: feature.type,
                patterns: {}
            };

            const modelId = this.addFeature(bucketFeature, bucketFeature.geometry, evaluationFeature);

            if (modelId) {
                // Since 3D model geometry extends over footprint or point geometry, it is important
                // to add some padding to envelope calculated for grid index lookup, in order to
                // prevent false negatives in FeatureIndex's coarse check.
                // Envelope padding is a half of featureIndex.grid cell size.

                // Padding is just and estimated suitable for models only covering <= 1/16 of tile EXTENTS.
                // Actual model bounds are not yet available at this stage. To compensate for larger models,
                // 'maxFeatureQueryRadius()' is used to query largest model bounds
                options.featureIndex.insert(feature, bucketFeature.geometry, index, sourceLayerIndex, this.index, this.instancesPerModel[modelId].instancedDataArray.length, EXTENT / 32);
            }
        }
        this.lookup = null;
    }

    evaluateQueryRenderedFeaturePadding() {
        const modelManager = this.layers[0].modelManager;
        const scope = this.layers[0].scope;

        let maxQueryRadius = 0;

        for (const modelId of this.modelUris) {
            const model = modelManager.getModel(modelId, scope);
            if (!model) {
                continue;
            }

            // Use max scaling of any instance of this model to ensure full extent is covered
            const modelInstances = this.instancesPerModel[modelId];
            if (modelInstances) {
                const radius = vec3.distance(model.aabb.max, model.aabb.min) * 0.5 * modelInstances.maxScale + modelInstances.maxXYTranslationDistance;
                const radiusInTileUnits = Math.min(EXTENT, Math.max(radius / this.tileToMeter, EXTENT / 32));

                maxQueryRadius = Math.max(radiusInTileUnits, maxQueryRadius);
            }
        }
        return maxQueryRadius;
    }

    update(states: FeatureStates, vtLayer: VectorTileLayer, availableImages: ImageId[], imagePositions: SpritePositions) {
        // called when setFeature state API is used
        for (const modelId in this.instancesPerModel) {
            const instances: PerModelAttributes = this.instancesPerModel[modelId];
            for (const id in states) {
                if (instances.idToFeaturesIndex.hasOwnProperty(id)) {
                    const feature = instances.features[instances.idToFeaturesIndex[id]];
                    this.evaluate(feature, states[id], instances, true);
                    this.uploaded = false;
                }
            }
        }
        this.maxHeight = 0; // needs to be recalculated.
    }

    updateZoomBasedPaintProperties(): boolean {
        if (!this.hasZoomDependentProperties) {
            return false;
        }

        // layer.paint.get('model-rotation').isZoom
        let reuploadNeeded = false;
        for (const modelId in this.instancesPerModel) {
            const instances = this.instancesPerModel[modelId];
            for (const feature of instances.features) {
                const layer = this.layers[0];
                const evaluationFeature = feature.feature;
                const canonical = this.canonical;

                const rotation = layer.paint.get('model-rotation').evaluate(evaluationFeature, {}, canonical);

                const scale = layer.paint.get('model-scale').evaluate(evaluationFeature, {}, canonical);

                const translation = layer.paint.get('model-translation').evaluate(evaluationFeature, {}, canonical);

                if (!vec3.exactEquals(feature.rotation, rotation) ||
                    !vec3.exactEquals(feature.scale, scale) ||
                    !vec3.exactEquals(feature.translation, translation)) {
                    this.evaluate(feature, feature.featureStates, instances, true);
                    reuploadNeeded = true;
                }
            }
        }
        return reuploadNeeded;
    }

    updateReplacement(coord: OverscaledTileID, source: ReplacementSource, layerIndex: number, scope: string): boolean {
        // Replacement has to be re-checked if the source has been updated since last time
        if (source.updateTime === this.replacementUpdateTime) {
            return false;
        }
        this.replacementUpdateTime = source.updateTime;

        // Check if replacements have changed
        const newReplacements = source.getReplacementRegionsForTile(coord.toUnwrapped(), true);
        if (regionsEquals(this.activeReplacements, newReplacements)) {
            return false;
        }

        this.activeReplacements = newReplacements;

        let reuploadNeeded = false;
        for (const modelId in this.instancesPerModel) {
            const perModelVertexArray: PerModelAttributes = this.instancesPerModel[modelId];
            const va = perModelVertexArray.instancedDataArray;

            for (const feature of perModelVertexArray.features) {
                const offset = feature.instancedDataOffset;
                const count = feature.instancedDataCount;

                for (let i = 0; i < count; i++) {
                    const i16 = (i + offset) * 16;

                    let x_ = va.float32[i16 + 0];
                    const wasHidden = x_ > EXTENT;
                    x_ = wasHidden ? x_ - EXTENT : x_;
                    const x = Math.floor(x_);
                    const y = Math.floor(va.float32[i16 + 1]);

                    let hidden = false;
                    for (const region of this.activeReplacements) {
                        if (skipClipping(region, layerIndex, LayerTypeMask.Model, scope)) continue;

                        if (region.min.x > x || x > region.max.x || region.min.y > y || y > region.max.y) {
                            continue;
                        }

                        const p = transformPointToTile(x, y, coord.canonical, region.footprintTileId.canonical);
                        hidden = pointInFootprint(p, region.footprint);

                        if (hidden) break;
                    }

                    va.float32[i16] = hidden ? x_ + EXTENT : x_;
                    reuploadNeeded = reuploadNeeded || (hidden !== wasHidden);
                }
            }
        }

        return reuploadNeeded;
    }

    isEmpty(): boolean {
        for (const modelId in this.instancesPerModel) {
            const perModelAttributes = this.instancesPerModel[modelId];
            if (perModelAttributes.instancedDataArray.length !== 0) return false;
        }
        return true;
    }

    uploadPending(): boolean {
        return !this.uploaded;
    }

    upload(context: Context) {
        // if buffer size is less than the threshold, do not upload instance buffer.
        // if instance buffer is not uploaded, instances are rendered one by one.
        const useInstancingThreshold = 0;
        if (!this.uploaded) {
            for (const modelId in this.instancesPerModel) {
                const perModelAttributes: PerModelAttributes = this.instancesPerModel[modelId];
                if (perModelAttributes.instancedDataArray.length < useInstancingThreshold || perModelAttributes.instancedDataArray.length === 0) continue;
                if (!perModelAttributes.instancedDataBuffer) {
                    perModelAttributes.instancedDataBuffer = context.createVertexBuffer(perModelAttributes.instancedDataArray, instanceAttributes.members, true, undefined, this.instanceCount);
                } else {
                    perModelAttributes.instancedDataBuffer.updateData(perModelAttributes.instancedDataArray);
                }
            }
        }
        this.uploaded = true;
    }

    destroy() {
        for (const modelId in this.instancesPerModel) {
            const perModelAttributes: PerModelAttributes = this.instancesPerModel[modelId];
            if (perModelAttributes.instancedDataArray.length === 0) continue;
            if (perModelAttributes.instancedDataBuffer) {
                perModelAttributes.instancedDataBuffer.destroy();
            }
        }
        const modelManager = this.layers[0].modelManager;
        if (modelManager && this.modelUris && this.modelsRequested) {
            for (const modelUri of this.modelUris) {
                modelManager.removeModel(modelUri, "", true);
            }
        }
    }

    addFeature(
        feature: BucketFeature,
        geometry: Array<Array<Point>>,
        evaluationFeature: EvaluationFeature,
    ): string {
        const layer = this.layers[0];
        const modelIdProperty = layer.layout.get('model-id');
        assert(modelIdProperty);

        const modelId = modelIdProperty.evaluate(evaluationFeature, {}, this.canonical);
        if (!modelId) {
            warnOnce(`modelId is not evaluated for layer ${layer.id} and it is not going to get rendered.`);
            return modelId;
        }
        // check if it's a valid model (absolute) URL
        if (isValidUrl(modelId, false)) {
            if (!this.modelUris.includes(modelId)) {
                this.modelUris.push(modelId);
            }
        } else {
            // Check if it's a style model
            if (this.styleDefinedModelURLs[modelId] !== undefined) {
                if (!this.modelUris.includes(modelId)) {
                    this.modelUris.push(modelId);
                }
            }
        }
        if (!this.instancesPerModel[modelId]) {
            this.instancesPerModel[modelId] = new PerModelAttributes();
        }

        const perModelVertexArray: PerModelAttributes = this.instancesPerModel[modelId];
        const instancedDataArray = perModelVertexArray.instancedDataArray;

        const modelFeature = new ModelFeature(evaluationFeature, instancedDataArray.length);
        for (const geometries of geometry) {
            for (const point of geometries) {
                if (point.x < 0 || point.x >= EXTENT || point.y < 0 || point.y >= EXTENT) {
                    continue; // Clip on tile borders to prevent duplicates
                }
                // reduce density
                if (this.lookupDim !== 0) {
                    const tileToLookup = (this.lookupDim - 1.0) / EXTENT;
                    const lookupIndex = this.lookupDim * ((point.y * tileToLookup) | 0) + (point.x * tileToLookup) | 0;
                    if (this.lookup) {
                        if (this.lookup[lookupIndex] !== 0) {
                            continue;
                        }
                        this.lookup[lookupIndex] = 1;
                    }
                }
                this.instanceCount++;
                const i = instancedDataArray.length;
                instancedDataArray.resize(i + 1);
                perModelVertexArray.instancesEvaluatedElevation.push(0);
                instancedDataArray.float32[i * 16] = point.x;
                instancedDataArray.float32[i * 16 + 1] = point.y;
            }
        }
        modelFeature.instancedDataCount = perModelVertexArray.instancedDataArray.length - modelFeature.instancedDataOffset;
        if (modelFeature.instancedDataCount > 0) {
            if (feature.id) {
                perModelVertexArray.idToFeaturesIndex[feature.id] = perModelVertexArray.features.length;
            }
            perModelVertexArray.features.push(modelFeature);
            this.evaluate(modelFeature, {}, perModelVertexArray, false);
        }
        return modelId;
    }

    getModelUris(): Array<string> {
        return this.modelUris;
    }

    evaluate(feature: ModelFeature, featureState: FeatureState, perModelVertexArray: PerModelAttributes, update: boolean) {
        const layer = this.layers[0];
        const evaluationFeature = feature.feature;
        const canonical = this.canonical;

        const rotation = feature.rotation = layer.paint.get('model-rotation').evaluate(evaluationFeature, featureState, canonical);

        const scale = feature.scale = layer.paint.get('model-scale').evaluate(evaluationFeature, featureState, canonical);

        const translation = feature.translation = layer.paint.get('model-translation').evaluate(evaluationFeature, featureState, canonical);

        // When layer.paint.get('model-color') is constant, evaluate returns the object defined in the spec.
        // If we don't create a new object here we would be updating that, causing problems afterwards
        const color = Object.assign({}, layer.paint.get('model-color').evaluate(evaluationFeature, featureState, canonical));
        color.a = layer.paint.get('model-color-mix-intensity').evaluate(evaluationFeature, featureState, canonical);

        const rotationScaleYZFlip = [];
        if (this.maxVerticalOffset < translation[2]) this.maxVerticalOffset = translation[2];

        // Track per model and per bucket maximum scaling as well as per instance translation
        const translationDistanceXYSq = translation[0] * translation[0] + translation[1] * translation[1];
        const translationDistanceXY = translationDistanceXYSq > 0 ? Math.sqrt(translationDistanceXYSq) : 0;
        perModelVertexArray.maxScale = Math.max(Math.max(perModelVertexArray.maxScale, scale[0]), Math.max(scale[1], scale[2]));
        perModelVertexArray.maxXYTranslationDistance = Math.max(perModelVertexArray.maxXYTranslationDistance, translationDistanceXY);
        this.maxScale = Math.max(Math.max(this.maxScale, scale[0]), Math.max(scale[1], scale[2]));

        rotationScaleYZFlipMatrix(rotationScaleYZFlip, rotation, scale);

        // https://github.com/mapbox/mapbox-gl-native-internal/blob/c380f9492220906accbdca1f02cca5ee489d97fc/src/mbgl/renderer/layers/render_model_layer.cpp#L1282
        const constantTileToMeterAcrossTile = 10;
        assert(perModelVertexArray.instancedDataArray.bytesPerElement === 64);

        const vaOffset2 = Math.round(100.0 * color.a) + color.b / 1.05;

        for (let i = 0; i < feature.instancedDataCount; ++i) {
            const instanceOffset = feature.instancedDataOffset + i;
            const offset = instanceOffset * 16;

            const va = perModelVertexArray.instancedDataArray.float32;
            let terrainElevationContribution = 0;
            if (update) {
                terrainElevationContribution = va[offset + 6] - perModelVertexArray.instancesEvaluatedElevation[instanceOffset];
            }

            // All per-instance attributes are packed to one 4x4 float matrix. Data is not expected
            // to change on every frame when e.g. camera or light changes.
            // Column major order. Elements:
            // 0 & 1: tile coordinates stored in integer part of float, R and G color components,
            // originally in range [0..1], scaled to range [0..0.952(arbitrary, just needs to be
            // under 1)].
            const pointY = va[offset + 1] | 0; // point.y stored in integer part
            va[offset] = (va[offset] | 0) + color.r / 1.05; // point.x stored in integer part
            va[offset + 1] = pointY + color.g / 1.05;
            // Element 2: packs color's alpha (as integer part) and blue component in fractional part.
            va[offset + 2] = vaOffset2;
            // tileToMeter is taken at center of tile. Prevent recalculating it over again for
            // thousands of trees.
            // Element 3: tileUnitsToMeter conversion.
            va[offset + 3] = 1.0 / (canonical.z > constantTileToMeterAcrossTile ? this.tileToMeter : tileToMeter(canonical, pointY));
            // Elements [4..6]: translation evaluated for the feature.
            va[offset + 4] = translation[0];
            va[offset + 5] = translation[1];
            va[offset + 6] = translation[2] + terrainElevationContribution;
            // Elements [7..16] Instance modelMatrix holds combined rotation and scale 3x3,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 7]  = rotationScaleYZFlip[0];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 8]  = rotationScaleYZFlip[1];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 9]  = rotationScaleYZFlip[2];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 10] = rotationScaleYZFlip[4];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 11] = rotationScaleYZFlip[5];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 12] = rotationScaleYZFlip[6];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 13] = rotationScaleYZFlip[8];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 14] = rotationScaleYZFlip[9];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            va[offset + 15] = rotationScaleYZFlip[10];
            perModelVertexArray.instancesEvaluatedElevation[instanceOffset] = translation[2];
        }
    }
}

register(ModelBucket, 'ModelBucket', {omit: ['layers']});
register(PerModelAttributes, 'PerModelAttributes');
register(ModelFeature, 'ModelFeature');

export default ModelBucket;
